import { Document } from "@/model/document2/document";
import { Page } from "@/model/document2/page/page";
import { Surface } from "@/model/document2/page/surface/surface";
import { Design } from "@/model/document2/page/surface/design/design";
import { Layer } from '@/model/document2/page/surface/design/layer/layer';

import { CompatibleHelper } from "@/helper/compatibleHelper";
import { ImageHelper } from "@/helper/imageHelper";
import { MathHelper } from "@/helper/mathHelper";
import { ImageLayer } from '@/model/document2/page/surface/design/layer/imageLayer';
import { FileHelper } from '@/helper/fileHelper';
import { RasterImageLayer } from '@/model/document2/page/surface/design/layer/rasterImageLayer';
import { VectorImageLayer } from '@/model/document2/page/surface/design/layer/vectorImageLayer';
import { GroupLayer } from '@/model/document2/page/surface/design/layer/groupLayer';
import { PlainTextLayer } from '@/model/document2/page/surface/design/layer/plainTextLayer';
import { TextInfo } from '@/utilityModels/textInfo';
import { ColorConstruct } from '@/utilityModels/colorConstruct';
import { UnitHelper } from '@/helper/unitHelper';
import { CircleLayer } from '@/model/document2/page/surface/design/layer/circleLayer';
import { Product } from '@/data/productCategory/product/product';
import { Paper } from '@/data/productCategory/product/paper/paper';
import { FrameInfo } from '@/data/productCategory/product/paper/frameInfo/frameInfo';
import { ShapeInfo } from '@/data/productCategory/product/paper/frameInfo/shapeInfo/shapeInfo';
import { FontManager } from '@/model/fontManager/fontManager';
import { RectangleLayer } from '@/model/document2/page/surface/design/layer/rectangleLayer';
import { Const } from '@/const';
import { Rectangle } from '@/utilityModels/rectangle';
import { CircleTextLayer } from '@/model/document2/page/surface/design/layer/circleTextLayer';
import { PolygonLayer } from '@/model/document2/page/surface/design/layer/polygonLayer';
import { LineLayer } from '@/model/document2/page/surface/design/layer/lineLayer';
import { Vector2 } from '@/utilityModels/vector2';
import { CurveLayer } from '@/model/document2/page/surface/design/layer/curveLayer';
import { ConvertErrorLayer } from '@/model/document2/page/surface/design/layer/convert/convertErrorLayer';

import { SvgResource } from "./util/svgResource";
import { ColumnData, RowData, CellData, InsertionData } from '@/model/document2/insertionData/insertionData';
import { TableLayer } from '@/model/document2/page/surface/design/layer/tableLayer';
import { TableCellInfo } from '@/model/document2/page/surface/design/layer/table/tableCellInfo';
import { D3TextLayer } from '@/model/document2/page/surface/design/layer/d3TextLayer';
import { BarcodeLayer } from '@/model/document2/page/surface/design/layer/barcodeLayer';
import { QRcodeLayer } from '@/model/document2/page/surface/design/layer/qrcodeLayer';
import { DateTextLayer } from '@/model/document2/page/surface/design/layer/dateTextLayer';
import { InsertionHelper } from "@/helper/insertionHelper";
import { DebugHelper } from "@/helper/debugHelper";
import { UserAgentHelper } from '@/helper/userAgentHelper';
import { SimpleTextLayer } from '@/model/document2/page/surface/design/layer/simpleTextLayer';

// 旧アプリケーションのファイルをコンバートする
export class FileConverter
{
    private _format = "";
    private _insertionEnabled = false;
    private _column = 0;
    private _paperRotation = 0;
    private _unloadedFonts : Array<string> = [];

    private _bitmapDictionary : { [ key : string] : { image : string, imageWhiteTransparent : string, width : number, height : number } } = {};

    private _fontManager : FontManager | null = null;

    constructor(fontManager : FontManager | null = null)
    {
        // フォントマネージャーを使うのはElectronのみ
        if(UserAgentHelper.isElectron())
        {
            this._fontManager = fontManager;
        }
    }

    private isNullValue(str)
    {
        return str.toUpperCase() == "null".toUpperCase();
    }

    private getElementByName(parent : any, elementName : string)
    {
        if(parent.elements != null && parent.elements instanceof Array)
        {
            for(var i = 0; i < parent.elements.length; ++i)
            {
                var el = parent.elements[i];
                if(el.name == elementName)
                {
                    return el;
                }
            }
        }
        return null;
    }

    private getPropertySafe(object : any, propertyName : string, defaltValue : string) : string
    {
        var value = defaltValue;
        if(object["attributes"] != null)
        {
            if(object["attributes"][propertyName] != null)
            if(typeof(object["attributes"][propertyName]) == "boolean")
            {
                value = object["attributes"][propertyName] ? "true" : "false";
            }
            else
            {
                value = "" + object["attributes"][propertyName];
            }
        }
        return value;
    }

    private convertNumberToColor(num : number) : ColorConstruct
    {
        var val = num;
        var b = val % 256;
        val = (val - b) / 256;
        var g = val % 256;
        val = (val - g) / 256;
        var r = val;

        return new ColorConstruct(r, g, b);
    }

    // Label9documentの基本単位はptなので、mmに変換する
    private pt2mm(num : number) : number
    {
        return UnitHelper.convert(num, "pt", "mm");
    }

    // Label9DocumentをDeserializeする
    // xml : Label9ファイル(xml)
    public deserializeLabel9FromXml(xml) : Document | null
    {
        // xml -> json
        var convert = require('xml-js');
        // ヌル文字を削除する
        xml = xml.replace(/&#x0;/g, "");
        var json = convert.xml2json(xml, { compact : false, ignoreComment : true, ignoreDoctype : true });
        return this.deserializeLabel9FromJson(json);
    }

    // Label9Document(jsonに変換したもの)をDeserializeする
    // json : Label9ファイル(xml)をjsonにしたもの
    public deserializeLabel9FromJson(json : string) : Document | null
    {
        return this.desalizeLabel9(JSON.parse(json));
    }

    private desalizeLabel9(jsonObject) : Document | null
    {
        DebugHelper.trace(jsonObject);
        try
        {
            var doc = this.getElementByName(jsonObject, "ngldoc");

            var i = 0;
            var j = 0;
            var k = 0;
            this._unloadedFonts = [];
            
            var version = this.getPropertySafe(doc, "version", "");
            var format = this.getPropertySafe(doc, "format", "");
            var category = Number(this.getPropertySafe(doc, "category_id", "0"));
            
            var format = this.isNullValue(format) ? format : "pc";
            this._insertionEnabled = this.getPropertySafe(doc, "insertionEnabled", "") == "true";

            // 差し込みで列優先かどうか
            var fillColumnsFirst = !(this.getPropertySafe(doc, "fillColumnsFirst", "") == "false");
            // 差し込みがONかどうか
            var insertionEnabled = this.getPropertySafe(doc, "insertionEnabled", "") == "true";

            var product = new Product();
            var paper = new Paper();
        
            // product, paperに用紙情報を読み込む。
            this.parseSheetSpec(this.getElementByName(doc, "sheet"), product, paper);

            var document = Document.create(product, paper);
            document.removePage(0);
            document.rotate = this._paperRotation;
            document.fileVersionLabel9 = version;
            document.useInsertion = insertionEnabled;
            document.insertionData.insertionDirection = fillColumnsFirst ? 0 : 1;

            // load bitmap data
            var bitmaps = this.getElementByName(doc, "bitmaps");
            var bitmapdataCount = bitmaps.elements != null ? bitmaps.elements.length : 0;
            for(i = 0; i < bitmapdataCount; ++i)
            {
                var bmddata = bitmaps.elements[i];

                var imgkey = this.getPropertySafe(bmddata, "imgkey", "");
                var sbmd = this.getPropertySafe(bmddata, "bmd", "");
                var bmdwidth = Number(this.getPropertySafe(bmddata, "width", "0"));
                var bmdheight = Number(this.getPropertySafe(bmddata, "height", "0"));

                // 文字列をARGBのバイト列にデコード
                var imageBytes = CompatibleHelper.convertCompressedEncodedImageToARGBByteArray(sbmd);
                
                // デコードしたバイト列からbase64文字列を取得
                var bmd = ImageHelper.convertARGBByteArrayToBase64(imageBytes, bmdwidth, bmdheight);
                var bmdWhiteTrans = ImageHelper.convertARGBByteArrayToBase64WhiteTransparent(imageBytes, bmdwidth, bmdheight);

                // keyとbase64文字列,width,heightを保持
                this._bitmapDictionary[imgkey] = { image : bmd, imageWhiteTransparent : bmdWhiteTrans, width : bmdwidth, height : bmdheight };
            }

            var pages = this.getElementByName(doc, "pages");
            var pageCount = pages.elements.length;
            for(i = 0; i < pageCount; ++i)
            {
                var data = pages.elements[i];

                // 新規ページ
                var page = document.createNewPage();
                this.parsePage(data, page, document);
                document.addPage(page);
            }

            // 差し込みデータ
            var columnInfo = this.getElementByName(doc, "cols");
            if(columnInfo != null)
            {
                var columns = this.parseColumnInfo(columnInfo);
                DebugHelper.trace(columns);
                document.insertionData.columnDataArray = columns;
            }

            var dataHeader = this.getElementByName(doc, "dataHeader");
            if(dataHeader != null)
            {
                var isFirstCoulumnIsTitle = this.getPropertySafe(dataHeader, "isFirstColumnIsTitle", "false") == "true";

                var row = this.getElementByName(dataHeader, "row");
                if(isFirstCoulumnIsTitle)
                {
                    document.insertionData.isFirstRowAsColumnName = true;

                    var data = this.getElementByName(doc, "data");
                    if(data != null)
                    {
                        var rowDataArray = this.parseDataWithHeader(data, dataHeader, document.insertionData);
                        document.insertionData.rowDataArray = rowDataArray;
                    }
                }
                else
                {
                    var data = this.getElementByName(doc, "data");
                    if(data != null)
                    {
                        var rowDataArray = this.parseData(data, document.insertionData);
                        document.insertionData.rowDataArray = rowDataArray;
                    }
                }
            }
            else
            {
                var data = this.getElementByName(doc, "data");
                if(data != null)
                {
                    var rowDataArray = this.parseData(data, document.insertionData);
                    document.insertionData.rowDataArray = rowDataArray;
                }
            }

            document.productCategoryId = category;
            document.unloadedFonts = this._unloadedFonts;

            if(insertionEnabled)
            {
                // 差し込み有効の時、最初のデザインを更新する
                InsertionHelper.applyInsertionToFirstDesign(document);
            }

            // 差し込み開始行と終了行をリセットする
            document.insertionData.startRow = 0;
            document.insertionData.endRow = document.insertionData.getRowDataCount() - 1;

            DebugHelper.trace("lcx document is converted successfully.");
            DebugHelper.trace(document);

            return document;
        }
        catch(ex)
        {
            DebugHelper.trace(ex);
            return null;
        }
    }

    private parseColumnInfo(data)
    {
        var columns : Array<ColumnData> = [];

        for(var i = 0; i < data.elements.length; ++i)
        {
            var column = data.elements[i];
            DebugHelper.trace(column);
            
            var headertext = this.getPropertySafe(column, "headertext", "");
            var datafield = this.getPropertySafe(column, "datafield", "");
            var prefix = this.getPropertySafe(column, "prefix", "");
            var postfix = this.getPropertySafe(column, "postfix", "");
            var alternative = this.getPropertySafe(column, "alternative", "");  // 使用しない

            var columnData = new ColumnData();
            columnData.headerText = headertext;
            columnData.insertionId = datafield;
            columnData.prefix = prefix;
            columnData.suffix = postfix;

            columns.push(columnData);
        }

        return columns;
    }

    private parseData(data, insertionData : InsertionData)
    {
        var j = 0;
        var array : Array<RowData> = [];

        for(var i = 0; i < data.elements.length; ++i)
        {
            var row = data.elements[i];
            var rowData = new RowData();
            for(var k = 0; k < insertionData.columnDataArray.length; ++k) { rowData.cellDataArray.push(new CellData()); }
            
            j = 0;
            while(true)
            {
                if(j >= row.elements.length) { break; }
                var col = row.elements[j];
                if(col == null) { break; }

                var key = this.getPropertySafe(col, "key", "");
                var d = this.getPropertySafe(col, "data", "");

                if(key == "print") 
                { 
                    // 印刷可能フラグ
                    rowData.isPrint = d == "true";
                }
                else if(key == "_repetition") 
                { 
                    // 印刷枚数
                    rowData.printCount = d == "" ? 0 : parseInt(d);
                }
                else
                {
                    // データ
                    // idが同じ場所に挿入する
                    for(var l = 0; l < insertionData.columnDataArray.length; ++l)
                    {
                        var columnData = insertionData.columnDataArray[l];
                        if(columnData.insertionId == key && l < rowData.cellDataArray.length)
                        {
                            rowData.cellDataArray[l].content = d;
                            break;
                        }
                    }
                }

                j++;
            }

            array.push(rowData);
        }

        return array;
    }

    private parseDataWithHeader(data, header, insertionData : InsertionData)
    {
        var array : Array<RowData> = [];

        var row = this.getElementByName(header, "row");
        var rowData = new RowData();
        for(var k = 0; k < insertionData.columnDataArray.length; ++k) { rowData.cellDataArray.push(new CellData()); }

        var j = 0;
        while(true)
        {
            if(j >= row.elements.length) { break; }
            var col = row.elements[j];
            if(col == null) { break; }

            var key = this.getPropertySafe(col, "key", "");
            var d = this.getPropertySafe(col, "data", "");

            if(key == "print") 
            { 
                // 印刷可能フラグ
                rowData.isPrint = d == "true";
            }
            else if(key == "_repetition") 
            { 
                // 印刷枚数
                rowData.printCount = d == "" ? 0 : parseInt(d);
            }
            else
            {
                // データ
                // idが同じ場所に挿入する
                for(var l = 0; l < insertionData.columnDataArray.length; ++l)
                {
                    var columnData = insertionData.columnDataArray[l];
                    if(columnData.insertionId == key && l < rowData.cellDataArray.length)
                    {
                        rowData.cellDataArray[l].content = d;
                        break;
                    }
                }
            }

            j++;
        }

        array.push(rowData);

        j = 0;
        for(var i = 0; i < data.elements.length; ++i)
        {
            var row = data.elements[i];
            var rowData = new RowData();
            for(var k = 0; k < insertionData.columnDataArray.length; ++k) { rowData.cellDataArray.push(new CellData()); }
            
            j = 0;
            while(true)
            {
                if(j >= row.elements.length) { break; }
                var col = row.elements[j];
                if(col == null) { break; }

                var key = this.getPropertySafe(col, "key", "");
                var d = this.getPropertySafe(col, "data", "");

                if(key == "print") 
                { 
                    // 印刷可能フラグ
                    rowData.isPrint = d == "true";
                }
                else if(key == "_repetition") 
                { 
                    // 印刷枚数
                    rowData.printCount = d == "" ? 0 : parseInt(d);
                }
                else
                {
                    // データ
                    // idが同じ場所に挿入する
                    for(var l = 0; l < insertionData.columnDataArray.length; ++l)
                    {
                        var columnData = insertionData.columnDataArray[l];
                        if(columnData.insertionId == key && l < rowData.cellDataArray.length)
                        {
                            rowData.cellDataArray[l].content = d;
                            break;
                        }
                    }
                }

                j++;
            }

            array.push(rowData);
        }

        return array;
    }

    // Document
    private parseSheetSpec(sheet, product : Product, paper : Paper) 
    {
        var productCode = this.getPropertySafe(sheet, "productCode", "");
        var productCodeWeb = this.getPropertySafe(sheet, "productCodeWeb", "");
        var label = this.getPropertySafe(sheet, "label", "");
        var maker = this.getPropertySafe(sheet, "maker", "");
        var description = this.getPropertySafe(sheet, "description", "");
        var purpose = this.getPropertySafe(sheet, "purpose", "");
        var paperdata = this.getPropertySafe(sheet, "paper", "");
        var paperwidth = Number(this.getPropertySafe(sheet, "paperWidth", "0"));
        var paperheight = Number(this.getPropertySafe(sheet, "paperHeight", "0"));
        var paperdirection = this.getPropertySafe(sheet, "paperDirection", "");
        var price = Number(this.getPropertySafe(sheet, "price", "0"));
        var pagination = this.getPropertySafe(sheet, "pagination", "");
        var surface = this.getPropertySafe(sheet, "surface", "");
        var note = this.getPropertySafe(sheet, "note", "");
        var paperRotation = Number(this.getPropertySafe(sheet, "paperRotation", "0"));
        var assortment = this.getPropertySafe(sheet, "assortment", "") == "true";
        var bleedW = Number(this.getPropertySafe(sheet, "bleedW", "0"));
        var bleedH = Number(this.getPropertySafe(sheet, "bleedH", "0"));
        var hasBleed = this.getPropertySafe(sheet, "hasBleed", "") == "true";
        var region = this.getPropertySafe(sheet, "region", "");
        var splitProductCode = this.getPropertySafe(sheet, "splitProductCode", "");
        var splitProductId = Number(this.getPropertySafe(sheet, "splitProductId", "0"));

        product.name = label;
        product.codeWeb = productCode;
        product.description = description;
        product.assortment = assortment;
        product.region = region;
        
        paper.name = label;
        paper.code = productCode;
        paper.surface = surface == "1,2" ? 2 : 1;
        paper.frameNumber = 0;
        paper.splitProductCode = splitProductCode;
        paper.width = paperwidth;
        paper.height = paperheight;
        paper.direction = paperdirection;
        this._paperRotation = paperRotation;

        var pieces = this.getElementByName(sheet, "pieces");
        var peaceCount = pieces.elements.length;
        paper.frameInfoList = new Array();

        for(var i = 0; i < peaceCount; ++i)
        {
            var piece = pieces.elements[i];
         
            var shape = this.getPropertySafe(piece, "shape", "");
            var columns = Number(this.getPropertySafe(piece, "columns", "0"));
            var rows = Number(this.getPropertySafe(piece, "rows", "0"));
            var leftmargin = Number(this.getPropertySafe(piece, "leftMargin", "0"));
            var topmargin = Number(this.getPropertySafe(piece, "topMargin", "0"));
            var horizontalpadding = Number(this.getPropertySafe(piece, "horizontalPadding", "0"));
            var verticalpadding = Number(this.getPropertySafe(piece, "verticalPadding", "0"));
            var width = Number(this.getPropertySafe(piece, "width", "0"));
            var height = Number(this.getPropertySafe(piece, "height", "0"));
            var innerdiameter = Number(this.getPropertySafe(piece, "innerDiameter", "0"));
            var cuttingmargin = Number(this.getPropertySafe(piece, "cuttingMargin", "0"));
            var useFrontFxg = this.getPropertySafe(piece, "useFrontFxg", "") == "true";
            var frontFxg = this.getPropertySafe(piece, "frontFxg", "");
            var useBackFxg = this.getPropertySafe(piece, "useBackFxg", "") == "true";
            var backFxg = this.getPropertySafe(piece, "backFxg", "");

            var frameInfo = new FrameInfo();
            frameInfo.shapeInfo = new ShapeInfo();
            frameInfo.orderNum = i;
            frameInfo.leftMargin = leftmargin;
            frameInfo.topMargin = topmargin;
            frameInfo.horizontalPadding = horizontalpadding;
            frameInfo.verticalPadding = verticalpadding;
            frameInfo.cuttingMargin = cuttingmargin;
            frameInfo.width = width;
            frameInfo.height = height;
            frameInfo.col = columns;
            frameInfo.row = rows;
            frameInfo.bleed = hasBleed;
            frameInfo.bleedWidth = bleedW;
            frameInfo.bleedHeight = bleedH;
            frameInfo.shapeInfo.name = shape;
            frameInfo.shapeInfo.innerDiameter = innerdiameter;
            frameInfo.shapeInfo.useFrontFxg = useFrontFxg ? 1 : 0;
            frameInfo.shapeInfo.frontFxg = frontFxg;
            frameInfo.shapeInfo.useBackFxg = useBackFxg ? 1 : 0;
            frameInfo.shapeInfo.backFxg = backFxg;
            frameInfo.shapeInfo.imageFormat = 0;

            paper.frameInfoList.push(frameInfo);

            paper.frameNumber += columns * rows;
        }
        
    }

    // Page
    private parsePage(data, page : Page, document : Document) 
    {
        var i = 0;
        var j = 0;
        var k = 0;

        var sideCount = data.elements != null ? data.elements.length : 0;
        for(i = 0; i < sideCount; ++i)
        {
            var side = data.elements[i];
            var labelCount = side.elements != null ? side.elements.length : 0;
            for(j = 0; j < labelCount; ++j)
            {
                var l = side.elements[j];
                this.parseLabel(l, page, i, document);
            }
        }
    }

    // Label(Design)
    private parseLabel(l, page : Page, side : number, document : Document) : void
    {
        var k = 0;

        var labelw = Number(this.getPropertySafe(l, "w", "0"));
        var labelh = Number(this.getPropertySafe(l, "h", "0"));
        // 初期拡大倍率
        var lablescale = Number(this.getPropertySafe(l, "scale", "0"));

        var label = Design.create(labelw, labelh);

        var backgroundtilemode = Number(this.getPropertySafe(l, "backgroundTileMode", "0"));
        var backgroundnnumoftile = Number(this.getPropertySafe(l, "backgroundNumOfTile", "0"));

        var hasBackground = this.getPropertySafe(l, "hasBackground", "") == "true";
        var backgroundCategory = this.getPropertySafe(l, "backgroundCategory", "");

        var backgroundImageKey = this.getPropertySafe(l, "backgroundImageKey", "");
        var showbackgroundcolor = this.getPropertySafe(l, "showBackgroundColor", "") == "true";
        var backgroundcolor = Number(this.getPropertySafe(l, "backgroundColor", "0"));

        var labelpiece = Number(this.getPropertySafe(l, "piece", "0"));
        var labelcol = Number(this.getPropertySafe(l, "col", "0"));
        var labelrow = Number(this.getPropertySafe(l, "row", "0"));

        var printable = this.getPropertySafe(l, "printable", "") == "true";
        var inverseVertical = this.getPropertySafe(l, "inverseVertical", "") == "true";
        var designRotationAngle = Number(this.getPropertySafe(l, "designRotationAngle", "0"));

        var surface = page.getSurface(side > 0);
        
        // positionを算出
        var position = this.getLabelPositionFromRowColumn(document, labelpiece, labelrow, labelcol, side > 0);
        if(position < 0)
        {
            // positionが異常なので、このラベルは読み飛ばす
            return;
        }

        surface.setDesign(position, label);
        label.width = this.pt2mm(labelw);
        label.height = this.pt2mm(labelh);

        // 背景設定
        // 最大裁ち落とし幅
        var maxBleedRect = new Rectangle(-Const.BLEED_SIZE_MAX, -Const.BLEED_SIZE_MAX, label.width + Const.BLEED_SIZE_MAX * 2, label.height + Const.BLEED_SIZE_MAX * 2);
        if(showbackgroundcolor)
        {
            // 背景色設定
            var bgColor = this.convertNumberToColor(backgroundcolor);
            if(bgColor.r == 255 && bgColor.g == 255 && bgColor.b == 255)
            {
                // 白背景は破棄する。
                DebugHelper.trace("white background is removed.");
            }
            else
            {
                var bgRectangleLayer = RectangleLayer.create(maxBleedRect.x, maxBleedRect.y, maxBleedRect.width, maxBleedRect.height);
                bgRectangleLayer.fillType = 1;
                bgRectangleLayer.fillColor = bgColor;
                bgRectangleLayer.strokeWidth = 0;
                bgRectangleLayer.name = "背景色";

                label.layer.addLayer(bgRectangleLayer);

                // 背景タイプ1 : 背景色あり
                document.backgroundTypeLabel9 = 1;
            }
        }
        if(hasBackground && backgroundImageKey != "")
        {
            // 背景画像設定
            var bgImage = this._bitmapDictionary[backgroundImageKey];
            if(backgroundtilemode == 1)
            {
                // フィット
                var fitImageLayer = RasterImageLayer.create(bgImage.image, maxBleedRect.x, maxBleedRect.y, maxBleedRect.width, maxBleedRect.height);
                fitImageLayer.name = "背景画像"
                fitImageLayer.rotate = label.rotate;
                fitImageLayer.setWhiteTransparentImage(bgImage.imageWhiteTransparent);
                label.layer.addLayer(fitImageLayer);
            }
            else if(backgroundtilemode == 2)
            {
                // 中央でトリム
                var bgWidth = UnitHelper.convert(bgImage.width, "pt", "mm");
                var bgHeight = UnitHelper.convert(bgImage.height, "pt", "mm");
                var x = maxBleedRect.centerX - bgWidth / 2;
                var y = maxBleedRect.centerY - bgHeight / 2;
                var trimImageLayer = RasterImageLayer.create(bgImage.image, x, y, bgWidth, bgHeight);
                trimImageLayer.name = "背景画像"
                trimImageLayer.setWhiteTransparentImage(bgImage.imageWhiteTransparent);
                label.layer.addLayer(trimImageLayer);
            }
            else if(backgroundtilemode == 4 && backgroundnnumoftile > 0)
            {
                // タイリング
                // タイル横個数
                var hTile = backgroundnnumoftile;
                // タイル縦個数
                var vTile = Math.ceil(hTile * maxBleedRect.height / maxBleedRect.width);
                var tileImageLayer = new RasterImageLayer();
                tileImageLayer.width = maxBleedRect.width;
                tileImageLayer.height = maxBleedRect.width * vTile / hTile;
                tileImageLayer.x = maxBleedRect.x;
                tileImageLayer.y = maxBleedRect.y;
                tileImageLayer.name = "背景画像"
                ImageHelper.tileImage(bgImage.image, hTile, vTile, (image)=>
                {
                    tileImageLayer.imageBase64 = image;
                    ImageHelper.createWhiteTransparentImage(image, (whiteTransImage) =>
                    {
                        trimImageLayer.setWhiteTransparentImage(whiteTransImage);
                    });
                });
                label.layer.addLayer(tileImageLayer);
            }

            // 背景タイプ2 : 背景画像あり
            document.backgroundTypeLabel9 = 2;
        }
        var components = this.getElementByName(l, "components");
        if(components)
        {
            var groupLayer = label.layer as GroupLayer;
            if(groupLayer != null)
            {
                this.parseComponents(components, label, groupLayer);
            }
        }
        
        // この角度で回転をかける
        label.rotateDesign(designRotationAngle);
    }

    private getLabelPositionFromRowColumn(document : Document, piece : number, row : number, col : number, isReverse : boolean = false)
    {
        var position = 0;
        for(var i = 0; i < document.frameArray.length; ++i)
        {
            var frame = document.frameArray[i];
            for(var r = 0; r < frame.row; ++r)
            {
                for(var c = 0; c < frame.col; ++c)
                {
                    if(isReverse)
                    {
                        // 裏の場合はcolの値を反転する
                        if(i == piece && r == row && c == frame.col - 1 - col)
                        {
                            return position;
                        }
                    }
                    else
                    {
                        if(i == piece && r == row && c == col)
                        {
                            return position;
                        }
                    }

                    position++;
                }
            }
        }
    
        // row, col の情報が間違ってる場合がある。その場合は-1を返す
        return -1;
    }

    // Layer
    private parseComponents(components, label : Design, groupLayer : GroupLayer, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0)
    {
        if(components.elements == null) { return; }   
        var componentCount = components.elements.length;
        for(var i = 0; i < componentCount; ++i)
        {
            var layer : Layer | null = null;
            var c = components.elements[i];
            if(c.name == "group")
            {
                layer = this.parseGroup(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "image")
            {
                layer = this.parseImage(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "text")
            {
                layer = this.parseText(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "circle")
            {
                layer = this.parseCircle(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "rectangle")
            {
                layer = this.parseRectangle(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "polygon")
            {
                layer = this.parsePolygon(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "pathtext")
            {
                layer = this.parsePathText(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "line")
            {
                layer = this.parseLine(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "spline")
            {
                layer = this.parseSpline(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "svg")
            {
                layer = this.parseSvg(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "table")
            {
                layer = this.parseTable(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "d3text")
            {
                layer = this.parse3dText(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "barcode")
            {
                layer = this.parseBarcode(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else if(c.name == "qr")
            {
                layer = this.parseQRCode(c, label, dx, dy, dScaleX, dScaleY, dRotation, centerX, centerY);
            }
            else
            {
                //! エラー書き出す
                layer = this.createConvertErrorLayer(c.name);
            }

            if(layer != null)
            {
                groupLayer.addLayer(layer);
            }
        }
    }

    // Error
    private createConvertErrorLayer(componentName, description = "") : Layer | null
    {
        var layer = new ConvertErrorLayer();
        layer.name = "convert_failed_" + componentName;
        layer.componentName = componentName;
        layer.description = description;

        return layer;
    }

    // Layer
    private parseGroup(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));

        var printable = this.getPropertySafe(c, "printable", "true") == "true";

        var comp = GroupLayer.create();

        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;

        var components = this.getElementByName(c, "components");

        var fixX = this.pt2mm(x);
        var fixY = this.pt2mm(y);

        // 子レイヤーへの補正
        var newdx = dx + fixX;
        var newdy = dy + fixY;
        var newdScaleX = dScaleX * scaleX;
        var newdScaleY = dScaleY * scaleY;

        var newdRotation = dRotation + rotation;

        if(components)
        {
            this.parseComponents(components, label, comp, newdx, newdy, newdScaleX, newdScaleY, newdRotation, fixX, fixY);
        }

        return comp;
    }

    private parseImage(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));

        var freezedAspectRatio = this.getPropertySafe(c, "freezeaspectratio", "") == "true";
        var transparentWhite = this.getPropertySafe(c, "transparentwhite", "") == "true";
        var invertHorizontal = this.getPropertySafe(c, "inverthorizontal", "") == "true";
        var invertVertical = this.getPropertySafe(c, "invertvertical", "") == "true";
        var lightColor = this.getPropertySafe(c, "lightcolor", "") == "true";
        var redOffset = Number(this.getPropertySafe(c, "redoffset", "0"));
        var greenOffset = Number(this.getPropertySafe(c, "greenoffset", "0"));
        var blueOffset = Number(this.getPropertySafe(c, "blueoffset", "0"));
        var brightNess =  Number(this.getPropertySafe(c, "brightness", "0"));

        var imagekey = this.getPropertySafe(c, "imgkey", "");
        
        var category = this.getPropertySafe(c, "category", "");

        var trimRect = this.getPropertySafe(c, "trimrect", "") == "true";
        var trimrectx = 0;
        var trimrecty = 0;
        var trimrectw = 0;
        var trimrecth = 0;
        if(trimRect)
        {
            trimrectx = Number(this.getPropertySafe(c, "trimrectx", "0"));
            trimrecty = Number(this.getPropertySafe(c, "trimrecty", "0"));
            trimrectw = Number(this.getPropertySafe(c, "trimrectw", "0"));
            trimrecth = Number(this.getPropertySafe(c, "trimrecth", "0"));
        }

        var imageWidth = Number(this.getPropertySafe(c, "imageWidth", "0"));
        var imageHeight = Number(this.getPropertySafe(c, "imageHeight", "0"));
        var imageBitmapData = this.getPropertySafe(c, "imageBitmapData", "");
        
        var printable = this.getPropertySafe(c, "printable", "true") == "true";

        var clipShape = this.getPropertySafe(c, "clipshape", "");
        var clipSVG = this.getPropertySafe(c, "clipsvg", "");
        var clipDirection = Number(this.getPropertySafe(c, "clipdirection", "0"));
        var clipX = Number(this.getPropertySafe(c, "clipx", "0"));
        var clipY = Number(this.getPropertySafe(c, "clipy", "0"));
        var clipW = Number(this.getPropertySafe(c, "clipw", "0"));
        var clipH = Number(this.getPropertySafe(c, "cliph", "0"));
        var imageRectX = Number(this.getPropertySafe(c, "imagerectx", "0"));
        var imageRectY = Number(this.getPropertySafe(c, "imagerecty", "0"));
        var imageRectW = Number(this.getPropertySafe(c, "imagerectw", "0"));
        var imageRectH = Number(this.getPropertySafe(c, "imagerecth", "0"));

        var comp : RasterImageLayer | VectorImageLayer | null = null;

        if(this._insertionEnabled && !(imagekey != "" && this._bitmapDictionary[imagekey]))
        {
            // 差し込みの場合
            // ラスターレイヤーを空画像で作成する
            comp = RasterImageLayer.create("", this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);
            // トリムはなし
            comp.isTrim = false;
        }
        else
        {
            // 通常
            if(!(imagekey != "" && this._bitmapDictionary[imagekey])) { return null; }
        
            var bmd = this._bitmapDictionary[imagekey];
            if(w > bmd.width && h > bmd.height)
            {
                var bmdScaleX = w / bmd.width;
                var bmdScaleY = h / bmd.height;

                w = bmd.width;
                h = bmd.height;

                trimrectw = bmd.width;
                trimrecth = bmd.height;

                scaleX *= bmdScaleX;
                scaleY *= bmdScaleY;
            }
            
            var isVector = bmd.image.indexOf("image/svg+xml") >= 0 || bmd.image.indexOf("image/svg-xml") >= 0;
            comp = isVector ? VectorImageLayer.create(bmd.image, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY) :
                                RasterImageLayer.create(bmd.image, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);

            comp.setWhiteTransparentImage(bmd.imageWhiteTransparent)

            // オリジナルの画像サイズ
            var orgImageWidth = bmd.width;
            var orgImageHeight = bmd.height;
            if(bmd.width > 400 && bmd.height > 400)
            {
                // 縦横が 400px を超えている場合は短辺 400px に縮小
                if(bmd.width < bmd.height)
                {
                    var imageScaleH = bmd.height / bmd.width;
                    orgImageWidth = 400;
                    orgImageHeight = 400 * imageScaleH;
                }
                else
                {
                    var imageScaleW = bmd.width / bmd.height;
                    orgImageHeight = 400;
                    orgImageWidth = 400 * imageScaleW;
                }
            }
            
            comp.imageWidth = orgImageWidth;
            comp.imageHeight = orgImageHeight;

            // トリム領域
            if(trimRect)
            {
                // トリムの計算がラベル9と異なるので、サイズと位置を変更する必要がある
                var fixWidth = comp.width / trimrectw * orgImageWidth;
                var fixHeight = comp.height / trimrecth * orgImageHeight;
                var fixX = comp.x - comp.width * trimrectx / trimrectw;
                var fixY = comp.y - comp.height * trimrecty / trimrecth;
                comp.x = fixX;
                comp.y = fixY;
                comp.width = fixWidth;
                comp.height = fixHeight;

                var trimx = MathHelper.clamp(trimrectx / orgImageWidth * 100, 0, 100);
                var trimy = MathHelper.clamp(trimrecty / orgImageHeight * 100, 0, 100);
                var trimw = MathHelper.clamp(trimrectw / orgImageWidth * 100, 0, 100);
                var trimh = MathHelper.clamp(trimrecth / orgImageHeight * 100, 0, 100);
                comp.isTrim = trimx > 0 || trimy > 0 || trimw < 100 || trimh < 100;
                // 誤差が出ることがあるので値を補正する
                if(trimx + trimw > 100) trimw = 100 - trimx;
                if(trimy + trimh > 100) trimh = 100 - trimy;
                comp.trimRectNormalized = new Rectangle(trimx, trimy, trimw, trimh);
            }
            else
            {
                comp.isTrim = false;
            }
        }

        if (comp == null) return null;

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.keepRatio = freezedAspectRatio ? 0 : 1;
        comp.whiteTransparent = transparentWhite;
        comp.flipHorizontal = invertHorizontal;
        comp.flipVertical = invertVertical;
        comp.opacity = MathHelper.roundByDigits(alpha * 100, 0);
        comp.colorFilterR = MathHelper.clamp(redOffset + brightNess, -255, 255);
        comp.colorFilterG = MathHelper.clamp(greenOffset + brightNess, -255, 255);
        comp.colorFilterB = MathHelper.clamp(blueOffset + brightNess, -255, 255);

        return comp;
    }

    private parseText(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));

        var recW = Number(this.getPropertySafe(c, "recw", "0"));
        var recH = Number(this.getPropertySafe(c, "rech", "0"));

        var origX = Number(this.getPropertySafe(c, "origx", "0"));
        var origY = Number(this.getPropertySafe(c, "origy", "0"));
        var requireSizeAdjustment = this.getPropertySafe(c, "requiresizeadjustment", "") == "true";

        var fontName = this.getPropertySafe(c, "fontname", "");
        var fontSize = Number(this.getPropertySafe(c, "fontsize", "12"));
        var textColor = Number(this.getPropertySafe(c, "textcolor", "0"));

        var blockProgressionIsVertical = this.getPropertySafe(c, "blockprogressionisvertical", "true") == "true";
        var bold = this.getPropertySafe(c, "bold", "true") == "true";
        var italic = this.getPropertySafe(c, "italic", "true") == "true";
        var underline = this.getPropertySafe(c, "underline", "true") == "true";
        var alignment = this.getPropertySafe(c, "alignment", "");
        var verticalAlignment = this.getPropertySafe(c, "verticalAlignment", "");

        var border = this.getPropertySafe(c, "border", "true") == "true";
        var borderColor = Number(this.getPropertySafe(c, "bordercolor", "0"));
        var shadow = this.getPropertySafe(c, "shadow", "true") == "true";
        var shadowColor = Number(this.getPropertySafe(c, "shadowcolor", "0"));

        var lineHeightStr = this.getPropertySafe(c, "lineheight", "120");
        var lineHeight = lineHeightStr == "" ? 120 : Number(lineHeightStr);
        var trackingLeft = Number(this.getPropertySafe(c, "trackingleft", "0"));
        var text = this.getPropertySafe(c, "text", "");

        var autoSizeFitMode = Number(this.getPropertySafe(c, "autosizefitmode", "0"));
        // 使用しない
        var isAutoLineHeight = this.getPropertySafe(c, "isautolineheight", "true") == "true";

        var printbale = this.getPropertySafe(c, "printable", "true") == "true";

        var fontSizeScale = Number(this.getPropertySafe(c, "fontsizescale", "0"));

        // 日付文字
        var autoDateFlag = this.getPropertySafe(c, "autoDateFlag", "0");
        // textのXML改行をJS改行に置き換える
        text = this.convertTextNewLine(text);

        // 日付文字or通常文字を作成する
        var comp = autoDateFlag == "1" ? DateTextLayer.create(this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY) : 
            SimpleTextLayer.create(text, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.horizontalAlignment = this.getHorizontalAlignment(alignment);
        comp.verticalAlignment = this.getVerticalAlignment(verticalAlignment);
        // autoSizeFitMode
        // 1: 自動 ON 自動折り返し OFF
        // 2: 自動 ON 自動折り返し ON
        // 4: 自動 OFF 自動折り返し OFF
        comp.autoSize = autoSizeFitMode == 1 || autoSizeFitMode == 2;
        if(comp instanceof SimpleTextLayer)
        {
            // 通常文字の場合
            // Lable9で両方オフのときもオンにする
            comp.autoNewLine = autoSizeFitMode != 1;
        }
        else
        {
            // 日付文字の場合
            comp.autoNewLine = autoSizeFitMode == 2;
        }
        comp.lineSpacing = lineHeight;

        comp.textOrientation = blockProgressionIsVertical ? 1 : 0;

        // 文法一斉変更
        var textInfo = new TextInfo();
        textInfo.fontId = this.getWebFontFromFontName(fontName);
        textInfo.letterSpacing = trackingLeft;
        // フォントサイズにはスケールを適用
        textInfo.fontSize = fontSize * Math.min(dScaleX, dScaleY);
        // fontsizeScale対応
        if(fontSizeScale > 0)
        {
            textInfo.fontSize *= 1 / fontSizeScale;
        }
        else if(fontSizeScale == 0)
        {
            var realFontSize = Number(this.getPropertySafe(c, "realfontsize", "0"));
            if(realFontSize > 0)
            {
                if(fontSize != realFontSize)
                {
                    textInfo.fontSize *= 1 / 10;
                }
            }
        }
        textInfo.textColor = this.convertNumberToColor(textColor);
        textInfo.bold = bold;
        textInfo.italic = italic;
        textInfo.underline = underline;
        textInfo.isShadow = shadow;
        textInfo.isOutline = border;
        textInfo.shadowColor = this.convertNumberToColor(shadowColor);
        textInfo.outlineColor = this.convertNumberToColor(borderColor);
        if(comp instanceof SimpleTextLayer)
        {
            comp.baseTextInfo = textInfo;
        }
        else if(comp instanceof DateTextLayer)
        {
            comp.textInfo = textInfo;

            // 日付文字の設定
            var autoDateYear = "1";
            var autoDateMonth = "1";
            var autoDateDate = "1";
            var autoDateDay = "0";
            var autoDateIncrementType = "2";
            var autoDateIncrementValue = 0;
            var autoDateSuffix = "";
            var autoDatePrefix = "";
            var autoDateSeparator = "0"
            var autoDatePaddingFlag = 1;

            var newAutoDateYear = this.getPropertySafe(c, "autoDateYear", "");
            if(newAutoDateYear.length > 0) { autoDateYear = newAutoDateYear; }
            var newAutoDateMonth = this.getPropertySafe(c, "autoDateMonth", "");
            if(newAutoDateMonth.length > 0) { autoDateMonth = newAutoDateMonth; }
            var newAutoDateDate = this.getPropertySafe(c, "autoDateDate", "");
            if(newAutoDateDate.length > 0) { autoDateDate = newAutoDateDate; }
            var newAutoDateDay = this.getPropertySafe(c, "autoDateDay", "");
            if(newAutoDateDay.length > 0) { autoDateDay = newAutoDateDay; }
            var newAutoDateIncrementType = this.getPropertySafe(c, "autoDateIncrementType", "");
            if(newAutoDateIncrementType.length > 0) { autoDateIncrementType = newAutoDateIncrementType; }
            var newAutoDateIncrementValue = this.getPropertySafe(c, "autoDateIncrementValue", "");
            if(newAutoDateIncrementValue.length > 0) { autoDateIncrementValue = Number(newAutoDateIncrementValue); }
            var newAutoDateSuffix = this.getPropertySafe(c, "autoDateSuffix", "");
            if(newAutoDateSuffix.length > 0) { autoDateSuffix = newAutoDateSuffix; }
            var newAutoDatePrefix = this.getPropertySafe(c, "autoDatePrefix", "");
            if(newAutoDatePrefix.length > 0) { autoDatePrefix = newAutoDatePrefix; }
            var newAutoDateSeparator = this.getPropertySafe(c, "autoDateSeparator", "");
            if(newAutoDateSeparator.length > 0) { autoDateSeparator = newAutoDateSeparator; }
            var newAutoDatePaddingFlag = this.getPropertySafe(c, "autoDatePaddingFlag", "");
            if(newAutoDatePaddingFlag.length > 0) { autoDatePaddingFlag = Number(newAutoDatePaddingFlag); }

            // 年
            // 0: なし
            // 1: 2018
            // 2: 18
            // 3: 平成30
            // 4: H30
            // 5: 30
            comp.yearType = Number(autoDateYear);

            // 月
            // 0: なし
            // 1: 8
            // 2: August
            // 3: Aug
            comp.monthType = Number(autoDateMonth);

            // 日
            // 0: なし
            // 1: 7
            comp.dayType = Number(autoDateDate);

            // 曜日
            // 0: なし
            // 1: TuesDay
            // 2: Tue
            // 3: (TuesDay)
            // 4: (Tue)
            // 5: 火
            // 6: 火曜
            // 7: 火曜日
            // 8: (火)
            // 9: (火曜)
            // 10: (火曜日)
            comp.weekdayType = Number(autoDateDay);

            // 選択中の増減タイプ
            // 0: 年
            // 1: 月
            // 2: 日
            comp.selectedDelta = Number(autoDateIncrementType);
            comp.dateDelta = autoDateIncrementValue;

            comp.suffix = autoDateSuffix;
            comp.prefix = autoDatePrefix;

            // 区切り
            // 0: 年月日
            // 1: ドット
            // 2: スラッシュ
            // 3: ハイフン
            // 4: スペース
            comp.splitType = Number(autoDateSeparator);

            // 月日2桁
            comp.is2Digits = autoDatePaddingFlag == 1;
        }

        return comp;
    }

    private getHorizontalAlignment(alignment)
    {
        if(alignment == "left") { return 0; }
        else if(alignment == "center") { return 1; }
        else if(alignment == "right") { return 2; }
        else if(alignment == "justify") { return 3; }
        return 0;
    }

    private getVerticalAlignment(alignment)
    {
        if(alignment == "top") { return 0; }
        else if(alignment == "middle") { return 1; }
        else if(alignment == "bottom") { return 2; }
        return 0;
    }

    private getWebFontFromFontName(fontName : string)
    {
        if(this._fontManager != null)
        {
            // まずフォントを検索する
            var fontFamily = this._fontManager.findFont(fontName);
            if(fontFamily != null)
            {
                // フォントが見つかったらこのフォントを使う
                return fontFamily.id;
            }
        }

        // フォント名追加 * 追加済みの場合は追加しない
        var pushed = false;
        for(var i = 0; i < this._unloadedFonts.length; ++i)
        {
            if(this._unloadedFonts[i] == fontName) 
            { 
                pushed = true; 
                break;
            }
        }
        if(!pushed)
        {
            this._unloadedFonts.push(fontName);
        }

        DebugHelper.trace("font convert map length : " + Const.FONT_CONVERT_MAP.length);

        var id = "";
        for(var i = 0; i < Const.FONT_CONVERT_MAP.length; ++i)
        {
            var pair = Const.FONT_CONVERT_MAP[i];
            if(pair.font == fontName)
            {
                id = pair.id;
                break;
            }
        }    

        // 規定以外は明朝かどうかで振り分ける
        if(id == "") { id = fontName.indexOf("明朝") >= 0 || fontName.indexOf("mincho") >= 0 ? FontManager.DEFAULT_MINCHO_FONT_ID : FontManager.DEFAULT_FONT_ID; }

        DebugHelper.trace("font convert : " + fontName + " > " + id);

        return id;
    }

    private parseCircle(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));

        var borderThickness = Number(this.getPropertySafe(c, "borderthickness", "0"));
        var borderColor = Number(this.getPropertySafe(c, "bordercolor", "0"));
        var fillColor = Number(this.getPropertySafe(c, "fillcolor", "0"));
        var fillOn = this.getPropertySafe(c, "fillon", "") == "true";

        var fillMode = this.getPropertySafe(c, "fillmode", "");
        if(fillMode == "none" || fillMode == "")
        {
            if(fillOn) { fillMode = "fill"; }
        }

        var gradColors = this.getPropertySafe(c, "gradcolors", "").split(",");
        var gradStartColor = gradColors.length > 0 ? Number(gradColors[0]) : 0;
        var gradEndColor = gradColors.length > 1 ? Number(gradColors[1]) : 0;

        var gradDirection = Number(this.getPropertySafe(c, "graddirection", "0"));

        // 9では線形しかない
        var gradType = this.getPropertySafe(c, "gradtype", "");

        var fillPattern = this.getPropertySafe(c, "fillpattern", "");
        var fillPatternWidth = Number(this.getPropertySafe(c, "fillpatternwidth", "0"));
        var fillPatternHeight = Number(this.getPropertySafe(c, "fillpatternheight", "0"));

        var printable = this.getPropertySafe(c, "printable", "true") == "true";

        var comp = CircleLayer.create(this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }
        
        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.opacity = MathHelper.clamp(alpha * 100, 0, 100);
        
        comp.strokeWidth = this.pt2mm(borderThickness);
        comp.strokeColor = this.convertNumberToColor(borderColor);
        comp.fillColor = this.convertNumberToColor(fillColor);
        
        if(fillMode == "none") { comp.fillType = 0; }
        else if(fillMode == "fill") { comp.fillType = 1; }
        else if(fillMode == "gradient") { comp.fillType = 2; }
        else { comp.fillType = 0; }

        comp.linearGradationAngle = gradDirection;
        comp.gradationType = gradType == "radial" ? 1 : 0;
        // 初期グラデーション削除
        comp.gradationInfoArray = new Array();
        comp.addGradation(0, this.convertNumberToColor(gradStartColor));
        comp.addGradation(100, this.convertNumberToColor(gradEndColor));
        
        return comp;
    }

    private parseRectangle(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));
        var borderThickness = Number(this.getPropertySafe(c, "borderthickness", "0"));
        var borderColor = Number(this.getPropertySafe(c, "bordercolor", "0"));
        var fillColor = Number(this.getPropertySafe(c, "fillcolor", "0"));
        var fillPattern = this.getPropertySafe(c, "fillpattern", "");
        var fillPatternWidth = Number(this.getPropertySafe(c, "fillpatternwidth", "0"));
        var fillPatternHeight = Number(this.getPropertySafe(c, "fillpatternheight", "0"));
        var borderPattern = this.getPropertySafe(c, "borderpattern", "");
        var borderPatternWidth = Number(this.getPropertySafe(c, "borderpatternwidth", "0"));
        var borderPatternHeight = Number(this.getPropertySafe(c, "borderpatternheight", "0"));
        var ellipseWidth = Number(this.getPropertySafe(c, "ellipsewidth", "0"));
        var ellipseHeight = Number(this.getPropertySafe(c, "ellipseheight", "0"));
        var fillOn = this.getPropertySafe(c, "fillon", "") == "true";

        var fillMode = this.getPropertySafe(c, "fillmode", "");
        if(fillMode == "none" || fillMode == "")
        {
            if(fillOn) { fillMode = "fill"; }
        }

        var gradColors = this.getPropertySafe(c, "gradcolors", "").split(",");
        var gradStartColor = gradColors.length > 0 ? Number(gradColors[0]) : 0;
        var gradEndColor = gradColors.length > 1 ? Number(gradColors[1]) : 0;

        var gradDirection = Number(this.getPropertySafe(c, "graddirection", "0"));

        var gradType = this.getPropertySafe(c, "gradtype", "");
        
        var printable = this.getPropertySafe(c, "printable", "true") == "true";

        var comp = RectangleLayer.create(this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }
        
        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.opacity = MathHelper.clamp(alpha * 100, 0, 100);
        
        comp.strokeWidth = this.pt2mm(borderThickness);
        comp.strokeColor = this.convertNumberToColor(borderColor);
        comp.fillColor = this.convertNumberToColor(fillColor);
        
        if(fillMode == "none") { comp.fillType = 0; }
        else if(fillMode == "fill") { comp.fillType = 1; }
        else if(fillMode == "gradient") { comp.fillType = 2; }
        else { comp.fillType = 0; }

        comp.linearGradationAngle = gradDirection;
        // 初期グラデーション削除
        comp.gradationInfoArray = new Array();
        comp.addGradation(0, this.convertNumberToColor(gradStartColor));
        comp.addGradation(100, this.convertNumberToColor(gradEndColor));

        // 丸み補正
        var ellipseScale = 1;
        var absScaleX = Math.abs(scaleX);
        var absScaleY = Math.abs(scaleY);

        if(absScaleX > absScaleY)
        {
            ellipseScale = scaleX / scaleY;
            ellipseWidth /= ellipseScale;
        }
        else
        {
            ellipseScale = scaleY / scaleX;
            ellipseHeight /= ellipseScale;
        }

        ellipseWidth = Math.abs(ellipseWidth);
        ellipseHeight = Math.abs(ellipseHeight);

        // これらの値は w,h のときの丸みの直径であるので、radiusを再設定する
        comp.horizontalRadius = MathHelper.clamp(ellipseWidth / w * 100, 0, 100);
        comp.verticalRadius =  MathHelper.clamp(ellipseHeight / h * 100, 0, 100);
        
        return comp;
    }

    private parsePolygon(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));
        var borderThickness = Number(this.getPropertySafe(c, "borderthickness", "0"));
        var borderColor = Number(this.getPropertySafe(c, "bordercolor", "0"));
        var fillColor = Number(this.getPropertySafe(c, "fillcolor", "0"));
        var borderPattern = this.getPropertySafe(c, "borderpattern", "");
        var borderPatternWidth = Number(this.getPropertySafe(c, "borderpatternwidth", "0"));
        var borderPatternHeight = Number(this.getPropertySafe(c, "borderpatternheight", "0"));
        var fillPattern = this.getPropertySafe(c, "fillpattern", "");
        var fillPatternWidth = Number(this.getPropertySafe(c, "fillpatternwidth", "0"));
        var fillPatternHeight = Number(this.getPropertySafe(c, "fillpatternheight", "0"));
        var vertexNum = Number(this.getPropertySafe(c, "vertexnum", "0"));
        var fillOn = this.getPropertySafe(c, "fillon", "") == "true";

        var fillMode = this.getPropertySafe(c, "fillmode", "");
        if(fillMode == "none" || fillMode == "")
        {
            if(fillOn) { fillMode = "fill"; }
        }

        var gradColors = this.getPropertySafe(c, "gradcolors", "").split(",");
        var gradStartColor = gradColors.length > 0 ? Number(gradColors[0]) : 0;
        var gradEndColor = gradColors.length > 1 ? Number(gradColors[1]) : 0;

        var gradDirection = Number(this.getPropertySafe(c, "graddirection", "0"));

        var gradType = this.getPropertySafe(c, "gradtype", "");
        
        var printable = this.getPropertySafe(c, "printable", "true") == "true";
        var roundDistance = Number(this.getPropertySafe(c, "roundDistance", "0"));

        // いったん正N角形作成
        var comp = PolygonLayer.create(vertexNum, true, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w), this.pt2mm(h));
        var orgX = this.pt2mm(x);
        var orgY = this.pt2mm(y);
        // 幅、高さ補正
        comp.width *= scaleX;
        comp.height *= scaleY;

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = orgX * dScaleX + dx;
        comp.y = orgY * dScaleY + dy;

        // 回転
        orgX = comp.x;
        orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }
        
        comp.name = name;
        comp.isLocked = locked;
        comp.opacity = MathHelper.clamp(alpha * 100, 0, 100);
        
        comp.strokeWidth = this.pt2mm(borderThickness);
        comp.strokeColor = this.convertNumberToColor(borderColor);
        comp.fillColor = this.convertNumberToColor(fillColor);

        if(fillMode == "none") { comp.fillType = 0; }
        else if(fillMode == "fill") { comp.fillType = 1; }
        else if(fillMode == "gradient") { comp.fillType = 2; }
        else { comp.fillType = 0; }

        comp.linearGradationAngle = gradDirection;
        comp.gradationType = gradType == "radial" ? 1 : 0;
        // 初期グラデーション削除
        comp.gradationInfoArray = new Array();
        comp.addGradation(0, this.convertNumberToColor(gradStartColor));
        comp.addGradation(100, this.convertNumberToColor(gradEndColor));

        comp.radius = roundDistance * 100;
			
        return comp;
    }

    private parsePathText(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));
        var initialScaleX = Number(this.getPropertySafe(c, "initialScaleX", "0"));
        var initialScaleY = Number(this.getPropertySafe(c, "initialScaleY", "0"));
        var text = this.getPropertySafe(c, "text", "");
        var fontSize = Number(this.getPropertySafe(c, "fontSize", "12"));
        var fontSizeInfimum = Number(this.getPropertySafe(c, "fontSizeInfimum", "12"));
        var color = Number(this.getPropertySafe(c, "color", "0"));
        var fontFamily = this.getPropertySafe(c, "fontFamily", "");
        var blockProgression = this.getPropertySafe(c, "blockProgression", "");
        var textAlign = this.getPropertySafe(c, "textAlign", "");
        var fontWeight = this.getPropertySafe(c, "fontWeight", "");
        var fontStyle = this.getPropertySafe(c, "fontStyle", "");
        var flip = this.getPropertySafe(c, "flip", "") == "true";
        var useUnderline = this.getPropertySafe(c, "useUnderline", "") == "true";
        var borderColor = Number(this.getPropertySafe(c, "borderColor", "0"));
        var useBorder = this.getPropertySafe(c, "useBorder", "") == "true";
        var shadowColor = Number(this.getPropertySafe(c, "shadowColor", "0"));
        var borderBlurXRatio = Number(this.getPropertySafe(c, "borderBlurXRatio", "0"));
        var borderBlurYRatio = Number(this.getPropertySafe(c, "borderBlurYRatio", "0"));
        var borderStrength = Number(this.getPropertySafe(c, "borderStrength", "0"));
        var useShadow = this.getPropertySafe(c, "useShadow", "") == "true";
        var shadowColor = Number(this.getPropertySafe(c, "shadowColor", "0"));
        var shadowDistanceRatio = Number(this.getPropertySafe(c, "shadowDistanceRatio", "0"));
        var shadowAngle = Number(this.getPropertySafe(c, "shadowAngle", "0"));
        var shadowBlurXRatio = Number(this.getPropertySafe(c, "shadowBlurXRatio", "0"));
        var shadowBlurYRatio = Number(this.getPropertySafe(c, "shadowBlurYRatio", "0"));
        var shadowStrength = Number(this.getPropertySafe(c, "shadowStrength", "0"));
        var lineHeight = Number(this.getPropertySafe(c, "lineHeight", "0"));
        var letterSpace = Number(this.getPropertySafe(c, "letterSpace", "0"));
        
        var printbale = this.getPropertySafe(c, "printable", "true") == "true";

        // textのXML改行をJS改行に置き換える
        text = this.convertTextNewLine(text);
        var comp = CircleTextLayer.create(text, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.opacity = alpha * 100;

        comp.textOrientation = blockProgression == "tl" ? 1 : 0;
        comp.direction = flip ? 1 : 0;

        comp.lineSpacing = lineHeight * 100 / fontSize; // 計算特殊です
        comp.horizontalAlignment = this.getHorizontalAlignment(textAlign);

        // 文法一斉変更
        var textInfo = new TextInfo();
        textInfo.fontId = this.getWebFontFromFontName(fontFamily);
        textInfo.letterSpacing = letterSpace;
        // フォントサイズにはスケールを適用
        textInfo.fontSize = fontSize * Math.min(dScaleX, dScaleY);
        textInfo.textColor = this.convertNumberToColor(color);
        textInfo.bold = fontWeight == "bold";
        textInfo.italic = fontStyle == "italic";
        textInfo.underline = useUnderline;
        textInfo.isShadow = useShadow;
        textInfo.shadowColor = this.convertNumberToColor(shadowColor);
        textInfo.shadowDistance = shadowDistanceRatio * 100;
        textInfo.shadowRotate = shadowAngle;
        textInfo.shadowBlur = MathHelper.clamp(Math.max(shadowBlurXRatio, shadowBlurYRatio) * 500, 0, 100);
        textInfo.shadowIntensity = MathHelper.clamp(shadowStrength * 100 / 2, 0, 100);
        textInfo.isOutline = useBorder;
        textInfo.outlineColor = this.convertNumberToColor(borderColor);
        textInfo.outlineThickness = MathHelper.clamp(Math.max(borderBlurXRatio, borderBlurYRatio) * 500, 0, 100);
        textInfo.outlineIntensity = MathHelper.clamp(borderStrength * 10 / 2, 0, 100);
        comp.setWholeTextInfo(textInfo);

        return comp;
    }

    private convertTextNewLine(text : string)
    {
        var result = text.replace(/&#x0A;/g, '\n').replace(/&#xA;/g, '\n');
        return result;
    }

    private parseLine(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));

        var x1 = Number(this.getPropertySafe(c, "x1", "0"));
        var y1 = Number(this.getPropertySafe(c, "y1", "0"));
        var x2 = Number(this.getPropertySafe(c, "x2", "0"));
        var y2 = Number(this.getPropertySafe(c, "y2", "0"));

        var lineThickness = Number(this.getPropertySafe(c, "linethickness", "0"));
        var linecolor = Number(this.getPropertySafe(c, "linecolor", "0"));

        var linePatternWidth = Number(this.getPropertySafe(c, "linepatternwidth", "0"));
        var linePatternHeight = Number(this.getPropertySafe(c, "linepatternheight", "0"));
        var linePattern = this.getPropertySafe(c, "fillpattern", "");   // <- 9 がこの値で保存してしまっている

        var leftArrow = this.getPropertySafe(c, "leftarrow", "");
        var leftArrowAngle = Number(this.getPropertySafe(c, "leftarrowangle", "0"));
        var leftArrowSize = Number(this.getPropertySafe(c, "leftarrowsize", "0"));

        var rightArrow = this.getPropertySafe(c, "rightarrow", "");
        var rightArrowAngle = Number(this.getPropertySafe(c, "rightarrowangle", "0"));
        var rightArrowSize = Number(this.getPropertySafe(c, "rightarrowsize", "0"));

        var printbale = this.getPropertySafe(c, "printable", "true") == "true";

        var comp = LineLayer.create(this.pt2mm(x1), this.pt2mm(y1), this.pt2mm(x2), this.pt2mm(y2));

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.opacity = alpha * 100;

        comp.strokeWidth = this.pt2mm(lineThickness);
        comp.strokeColor = this.convertNumberToColor(linecolor);

        comp.startType = this.lineArrowTypeToNumber(leftArrow);
        comp.startWidth = this.pt2mm(leftArrowSize);

        comp.endType = this.lineArrowTypeToNumber(rightArrow);
        comp.endWidth = this.pt2mm(rightArrowSize);

        return comp;
    }

    // 0 : 通常
    // 1 : 単線矢印
    // 2 : 塗りつぶし矢印
    // 3 : 矢じり
    // 4 : 丸
    // 5 : ひし形
    private lineArrowTypeToNumber(arrowType : string)
    {
        var num = 0;
        if(arrowType == "line") { num = 1; }
        else if(arrowType == "triangle") { num = 2; }
        else if(arrowType == "arrowhead") { num = 3; }
        else if(arrowType == "circle") { num = 4; }
        else if(arrowType == "rhombus") { num = 5; }
        return num;
    }

    private parseSpline(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var lineColor = Number(this.getPropertySafe(c, "lineColor", "0"));
        var lineThickness = Number(this.getPropertySafe(c, "lineThickness", "0"));

        var printbale = this.getPropertySafe(c, "printable", "true") == "true";

        var comp = new CurveLayer();
        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.strokeColor = this.convertNumberToColor(lineColor);
        comp.strokeWidth = this.pt2mm(lineThickness);

        var controlPoints = this.getElementByName(c, "controlPoints");
        if(controlPoints != null && controlPoints.elements != null && controlPoints.elements instanceof Array)
        {
            var controlPointsCount = controlPoints.elements.length;
            if(controlPointsCount >= 3)
            {
                for(var i = 0; i < controlPointsCount; ++i)
                {
                    var point = controlPoints.elements[i];
                    var px = Number(this.getPropertySafe(point, "x", "0")) + x;
                    var py = Number(this.getPropertySafe(point, "y", "0")) + y;
                    let cpx = this.pt2mm(px) * dScaleX + dx;
                    let cpy = this.pt2mm(py) * dScaleY + dy;
                    // 回転対応
                    let rotp = (new Vector2(cpx, cpy)).rotateDegrees(dRotation, centerX, centerY);
                    comp.addControlPoint(rotp.x, rotp.y);
                }
            }
        }
        else 
        {
            return null;
        }

        return comp;
    }

    private parseSvg(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scaleX = Number(this.getPropertySafe(c, "scalex", "0"));
        var scaleY = Number(this.getPropertySafe(c, "scaley", "0"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "0"));
        var freezedAspectRatio = this.getPropertySafe(c, "freezeaspectratio", "") == "true";
        var printbale = this.getPropertySafe(c, "printable", "true") == "true";
        var mobile = this.getPropertySafe(c, "mobileEnabled", "");
        var mobileEnabled = mobile == "1";
        var svgName = this.getPropertySafe(c, "svgName", "");
        var fillColor = Number(this.getPropertySafe(c, "fillcolor", "0"));
        var category = this.getPropertySafe(c, "category", "");
        var svg = this.getPropertySafe(c, "svg", "");

        // imageSource
        var svgTag : string | null = "";
        var svgImage = "";
        if(svgName == "001")
        {
            svgTag = CompatibleHelper.convertCompressedEncodedImageToSvg(SvgResource.SVG_001);
            if(svgTag != null)
            {
                svgTag = FileConverter.changeFillColor(svgTag, this.convertNumberToColor(fillColor));
                svgImage = ImageHelper.convertSVGTagToBase64(svgTag);
            }
        }
        else if(svgName == "011")
        {
            svgTag = CompatibleHelper.convertCompressedEncodedImageToSvg(SvgResource.SVG_011);
            if(svgTag != null)
            {
                svgTag = FileConverter.changeFillColor(svgTag, this.convertNumberToColor(fillColor));
                svgImage = ImageHelper.convertSVGTagToBase64(svgTag);
            }
        }
        // else if(svgName == "020")
        // {
        //     svgTag = CompatibleHelper.convertCompressedEncodedImageToSvg(SvgResource.SVG_020);
        //     if(svgTag != null)
        //     {
        //         svgTag = FileConverter.changeFillColor(svgTag, this.convertNumberToColor(fillColor));
        //         svgImage = ImageHelper.convertSVGTagToBase64(svgTag);
        //     }
        // }
        else
        {
            svgTag = CompatibleHelper.convertCompressedEncodedImageToSvg(svg);
            if(svgTag != null)
            {
                svgTag = FileConverter.changeFillColor(svgTag, this.convertNumberToColor(fillColor));
                svgTag = FileConverter.changeSvgViewSize(svgTag, w, h);
                svgImage = ImageHelper.convertSVGTagToBase64(svgTag);
            }
        }

        var comp = VectorImageLayer.create(svgImage, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scaleX, this.pt2mm(h) * scaleY);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.insertionId = mapKey;
        comp.isLocked = locked;
        comp.opacity = alpha * 100;

        comp.keepRatio = freezedAspectRatio ? 0 : 1;

        return comp;
    }

    // svg の path のfillColorを差し替える
    private static changeFillColor(tag : string, color : ColorConstruct)
    {
        var result = "";
        try
        {
            // パース
            var parser = new DOMParser();
            var xmlDocument = parser.parseFromString(tag, "image/svg+xml");
            // "path" タグを取得する
            var pathList = xmlDocument.getElementsByTagNameNS("http://www.w3.org/2000/svg", "path");
            for(var i = 0; i < pathList.length; ++i)
            {
                var item = pathList[i];

                // fill差し替え
                var attrs = item.attributes;
                if(attrs.getNamedItem("fill") != null)
                {
                    attrs.removeNamedItem("fill");
                }
                var newAttr = xmlDocument.createAttribute("fill");
                newAttr.value = color.hex;
                attrs.setNamedItem(newAttr);
            }

            var serializer = new XMLSerializer();
            result = serializer.serializeToString(xmlDocument);
        }
        catch(e)
        {
            DebugHelper.trace(e);
            result = tag;
        }

        return result;
    }

    // svgのViewBox, width, heightの値を差し替える
    private static changeSvgViewSize(tag : string, width : number, height : number)
    {
        var result = "";
        try
        {
            // パース
            var parser = new DOMParser();
            var xmlDocument = parser.parseFromString(tag, "image/svg+xml");
            // "svg" タグを取得する
            var pathList = xmlDocument.getElementsByTagNameNS("http://www.w3.org/2000/svg", "svg");
            for(var i = 0; i < pathList.length; ++i)
            {
                var item = pathList[i];

                var attrs = item.attributes;

                // width差し替え
                if(attrs.getNamedItem("width") != null)
                {
                    attrs.removeNamedItem("width");
                    var newAttr = xmlDocument.createAttribute("width");
                    newAttr.value = width + "px";
                    attrs.setNamedItem(newAttr);
                }
                // height差し替え
                if(attrs.getNamedItem("height") != null)
                {
                    attrs.removeNamedItem("height");
                    var newAttr = xmlDocument.createAttribute("height");
                    newAttr.value = height + "px";
                    attrs.setNamedItem(newAttr);
                }
                // viewBox差し替え
                let viewBox = attrs.getNamedItem("viewBox");
                if(viewBox != null && viewBox.value != null)
                {
                    let arr = viewBox.value.split(' ');
                    if(arr.length >= 2)
                    {
                        attrs.removeNamedItem("viewBox");
                        var newAttr = xmlDocument.createAttribute("viewBox");
                        newAttr.value = arr[0] + " " + arr[1] + " " + width + " " + height;
                        attrs.setNamedItem(newAttr);
                    }
                }
            }

            var serializer = new XMLSerializer();
            result = serializer.serializeToString(xmlDocument);
        }
        catch(e)
        {
            DebugHelper.trace(e);
            result = tag;
        }

        return result;
    }

    private parseTable(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var i = 0;
        var j = 0;

        var id = Number(this.getPropertySafe(c, "id", "0"));
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scalex = Number(this.getPropertySafe(c, "scalex", "1"));
        var scaley = Number(this.getPropertySafe(c, "scaley", "1"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "1"));

        var printable = this.getPropertySafe(c, "printable", "") == "true";

        var columnCount = 1;
        var columns = this.getElementByName(c, "columns");
        if(columns != null) { columnCount = columns.elements.length; }

        var rowCount = 1;
        var rows = this.getElementByName(c, "rows");
        if(rows != null) { rowCount = rows.elements.length; }

        var columnWidthArray : Array<number> = [];
        var columnWidthSum = 0;
        for(var column = 0; column < columnCount; ++column)
        {
            var columnData = columns.elements[column];
            var headerText = this.getPropertySafe(columnData, "headerText", "");
            var columnWidth = Number(this.getPropertySafe(columnData, "width", "0"));
            columnWidthArray.push(columnWidth);
            columnWidthSum += columnWidth;
        }
        
        var rowHeightArray : Array<number> = [];
        var rowHeightSum = 0;
        for(var row = 0; row < rowCount; ++row)
        {
            var rowData = rows.elements[row];
            var rowHeight = Number(this.getPropertySafe(rowData, "rowHeight", "0"));

            rowHeightArray.push(rowHeight);
            rowHeightSum += rowHeight;
        }

        // 表の幅・高さはw, h の値ではなく、column, rowのパラメータから求まる
        var comp = TableLayer.create(this.pt2mm(x), this.pt2mm(y), this.pt2mm(columnWidthSum) * scalex, this.pt2mm(rowHeightSum) * scaley, rowCount, columnCount);
        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.isLocked = locked;
        comp.insertionId = mapKey;
        
        comp.strokeWidth = this.pt2mm(Number(this.getPropertySafe(c, "lineThickness", "0")));
        // 表の線の太さは設定値より実際は細くなっている
        comp.strokeWidth = Math.max(UnitHelper.convert(comp.strokeWidth, "pt", "mm"), 0.01);
        var lineColor = Number(this.getPropertySafe(c, "lineColor", "0"));
        comp.strokeColor = this.convertNumberToColor(lineColor);

        if(columnWidthSum > 0 && rowHeightSum > 0)
        {
            for(var co = 0; co < comp.column; ++co)
            {
                var widthNormalized = columnWidthArray[co] / columnWidthSum * 100;
                for(var ro = 0; ro < comp.row; ++ro)
                {
                    var heightNormalized = rowHeightArray[ro] / rowHeightSum * 100;

                    var cellInfo = comp.getCellInfo(ro, co);
                    cellInfo.cellWidthNormalized = widthNormalized;
                    cellInfo.cellHeightNormalized = heightNormalized;
                    comp.setCellInfo(ro, co, cellInfo);
                }
            }
        }

        return comp;
    }

    private parse3dText(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scalex = Number(this.getPropertySafe(c, "scalex", "1"));
        var scaley = Number(this.getPropertySafe(c, "scaley", "1"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "1"));
        var lightx = Number(this.getPropertySafe(c, "lightx", "0"));
        var lighty = Number(this.getPropertySafe(c, "lighty", "0"));
        var lightz = Number(this.getPropertySafe(c, "lightz", "0"));
        var lightingMaterialColor = Number(this.getPropertySafe(c, "lightingmaterialcolor", "0"));
        var lightingMaterialAlpha = Number(this.getPropertySafe(c, "lightingmaterialalpha", "1"));
        var lightingMaterialAmbient = Number(this.getPropertySafe(c, "lightingmaterialambient", "0"));
        var lightingMaterialDiffusion = Number(this.getPropertySafe(c, "lightingmaterialdiffusion", "0"));
        var lightingMaterialSpecular = Number(this.getPropertySafe(c, "lightingmaterialspecular", "0"));
        var lightingMaterialPower = Number(this.getPropertySafe(c, "lightingmaterialpower", "0"));
        var lightingMaterialEmittance = Number(this.getPropertySafe(c, "lightingmaterialemittance", "0"));
        var lightingMaterialDoubleSided = Number(this.getPropertySafe(c, "lightingmaterialdoublesided", "0"));
        var rotx = Number(this.getPropertySafe(c, "rotx", "0"));
        var roty = Number(this.getPropertySafe(c, "roty", "0"));
        var rotz = Number(this.getPropertySafe(c, "rotz", "0"));
        var text = this.getPropertySafe(c, "text", "");
        var fontSize = Number(this.getPropertySafe(c, "fontsize", "0"));
        var font = this.getPropertySafe(c, "font", "");
        var thickness = Number(this.getPropertySafe(c, "thickness", "0"));
        var zoom = Number(this.getPropertySafe(c, "zoom", "0"));
        var focus = Number(this.getPropertySafe(c, "focus", "0"));
        var is3d = this.getPropertySafe(c, "is3d", "true") == "true";
        var initilaHeight = Number(this.getPropertySafe(c, "initialheight", "0"));

        var printable = this.getPropertySafe(c, "printable", "") == "true";

        var comp = D3TextLayer.create(text, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scalex, this.pt2mm(h) * scaley);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.isLocked = locked;
        comp.name = name;
        comp.insertionId = mapKey;
        comp.opacity = lightingMaterialAlpha * 100;
        comp.lightX = lightx;
        comp.lightY = lighty;
        comp.lightZ = lightz;
        comp.textColor = this.convertNumberToColor(lightingMaterialColor);
        comp.fontId = this.getWebFontFromFontName(font);
        comp.reflect = lightingMaterialAmbient;
        comp.brightness = lightingMaterialEmittance;
        comp.rotateX = rotx;
        comp.rotateY = roty;
        comp.rotateZ = rotz;
        comp.text = text;
        comp.thickness = thickness;

        return comp;
    }

    private parseBarcode(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scalex = Number(this.getPropertySafe(c, "scalex", "1"));
        var scaley = Number(this.getPropertySafe(c, "scaley", "1"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "1"));

        var type = this.getPropertySafe(c, "type", "");
        var text = this.getPropertySafe(c, "text", "");
        var barHeight = Number(this.getPropertySafe(c, "barHeight", "0"));
        var barWidth = Number(this.getPropertySafe(c, "barWidth", "0"));
        var showQuietZone = this.getPropertySafe(c, "showQuietZone", "") == "true";
        var showCharacter = this.getPropertySafe(c, "showCharacter", "") == "true";
        var nwRatio = Number(this.getPropertySafe(c, "nwRatio", "1"));

        var printable = this.getPropertySafe(c, "printable", "") == "true";

        var comp = BarcodeLayer.create(this.convertBarcodeType(type), text, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scalex, this.pt2mm(h) * scaley);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.isLocked = locked;
        comp.insertionId = mapKey;
        comp.showNumber = showCharacter;

        return comp;
    }

    // 0: JAN(標準) (EAN-13)
    // 1: JAN(短縮) (EAN-8)
    // 2: 物流(標準) (ITF-14)
    // 3: 物流(拡張) (ITF)
    // 4: 物流(アドオン) (ITF)
    // 5: ITF (ITF)
    // 6: NW-7 (Codabar)
    // 7: CODE39 (CODE39)
    // 8: CODE128 (CODE128)
    private convertBarcodeType(type : string)
    {
        var t = 0;
        if(type == "ean13") { t = 0; }
        else if(type == "ean8") { t = 1; }
        else if(type == "jisx0502-standard") { t = 2; }
        else if(type == "jisx0502-extended") { t = 3; }
        else if(type == "jisx0502-addon") { t = 4; }
        else if(type == "int25") { t = 5; }
        else if(type == "codabar") { t = 6; }
        else if(type == "code39") { t = 7; }
        else if(type == "code128") { t = 8; }

        return t;
    }

    private parseQRCode(c, label : Design, dx = 0, dy = 0, dScaleX = 1, dScaleY = 1, dRotation = 0, centerX = 0, centerY = 0) : Layer | null
    {
        var id = Number(this.getPropertySafe(c, "id", "0"));
        var w = Number(this.getPropertySafe(c, "w", "0"));
        var h = Number(this.getPropertySafe(c, "h", "0"));
        var name = this.getPropertySafe(c, "name", "");
        var locked = this.getPropertySafe(c, "locked", "") == "true";
        var mapKey = this.getPropertySafe(c, "mapkey", "");
        var x = Number(this.getPropertySafe(c, "x", "0"));
        var y = Number(this.getPropertySafe(c, "y", "0"));
        var scalex = Number(this.getPropertySafe(c, "scalex", "1"));
        var scaley = Number(this.getPropertySafe(c, "scaley", "1"));
        var rotation = Number(this.getPropertySafe(c, "rotation", "0"));
        var alpha = Number(this.getPropertySafe(c, "alpha", "1"));

        var text = this.getPropertySafe(c, "text", "");
        var auto = this.getPropertySafe(c, "auto", "") == "true";
        var version = Number(this.getPropertySafe(c, "version", "1"));
        var cellSize = Number(this.getPropertySafe(c, "cellSize", "0"));
        var errorCorrentionLevel = Number(this.getPropertySafe(c, "errorCorrectionLevel", "0"));

        // 旧バージョンと識別
        var hasMargin = -1;
        if(this.getPropertySafe(c, "hasMargin", "") != "")
        {
            hasMargin = Number(this.getPropertySafe(c, "hasMargin", ""))
        }

        var createdVersion = "";
        if(this.getPropertySafe(c, "createdVersion", "") != "")
        {
            createdVersion = this.getPropertySafe(c, "createdVersion", "");
        }

        var printable = this.getPropertySafe(c, "printable", "") == "true";

        var comp = QRcodeLayer.create(text, this.pt2mm(x), this.pt2mm(y), this.pt2mm(w) * scalex, this.pt2mm(h) * scaley);

        // サイズ・位置補正
        comp.width *= dScaleX;
        comp.height *= dScaleY;
        comp.x = comp.x * dScaleX + dx;
        comp.y = comp.y * dScaleY + dy;

        // 回転
        var orgX = comp.x;
        var orgY = comp.y;
        comp.rotate = rotation;
        comp.x = orgX;
        comp.y = orgY;

        if(dRotation != 0)
        {
            // x, y点をdRotation回転
            var rotated = (new Vector2(comp.x, comp.y)).rotateDegrees(dRotation, centerX, centerY);
            comp.rotate += dRotation;
            // x, yをその点に移動
            comp.x = rotated.x;
            comp.y = rotated.y;
        }

        comp.name = name;
        comp.isLocked = locked;
        comp.insertionId = mapKey;
        
        comp.version = auto ? 0 : version;
        comp.margin = hasMargin == 1;
        comp.errorCorrection = this.convertErrorCorrection(errorCorrentionLevel);

        return comp;
    }

    private convertErrorCorrection(level : number)
    {
        var c = 0;
        if(level == 0) { c = 1; }
        else if(level == 1) { c = 0; }
        else if(level == 2) { c = 3; }
        else if(level == 3) { c = 2; }

        return c;
    }
}